import BigNumber from 'bignumber.js';
import { Platform } from 'react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatAmountByDecimals } from '../../../utils/numberFormatting';

interface Selection {
  start: number;
  end: number;
}

interface RawAmountInputProps {
  inputValue: string;
  inputSelection: {
    start: number;
    end: number;
  };
}

interface UseAmountInputProps {
  value?: string | number;
  decimals?: number;
  min?: string | number;
  max?: string | number;
  onValueChange?: (value?: number, isMax?: boolean) => void;
}

export const useAmountInput = (props?: UseAmountInputProps) => {
  const { value, decimals = 6, onValueChange, max } = props || {};

  const [inputValueAndSelection, setInputValueAndSelection] = useState<{
    inputValue: string;
    inputSelection: Selection;
  }>({ inputValue: '', inputSelection: { start: 0, end: 0 } });
  const [valueBig, setValueBig] = useState<BigNumber | null>(null);
  const [isMax, setIsMax] = useState<boolean>(false);

  const [displayValue, setDisplayValue] = useState<string>('');
  const [selection, setSelection] = useState<Selection>({ start: 0, end: 0 });

  useEffect(() => {
    formatInput();
  }, [inputValueAndSelection.inputValue]);

  useEffect(() => {
    console.log('@onValueChange', valueBig ? valueBig.toNumber() : undefined, isMax);
    if (valueBig == undefined) return;
    onValueChange && onValueChange(valueBig.toNumber(), isMax);
  }, [valueBig]);

  /**
   * On Init
   */
  useEffect(() => {
    if (value !== undefined) {
      const rawDefaultValue = typeof value === 'string' ? value.replace(/[^0-9.]/g, '') : value;
      const inputValue = new BigNumber(rawDefaultValue).toString();
      setInputValueAndSelection({
        inputValue,
        inputSelection: { start: 0, end: 0 }
      });
    }
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      formatValue(value);
    }
  }, [value]);

  /** on Set Max */
  function onSetMax() {
    if (max === undefined) return;
    let rawBalance = typeof max === 'string' ? max.replace(/[^0-9\.]/g, '') : max;
    let balance = new BigNumber(rawBalance).toString();
    setInputValueAndSelection({
      inputValue: balance,
      inputSelection: { start: 0, end: 0 }
    });
    setIsMax(true);
  }

  /**
   * On Change Value
   */
  const onChange = useCallback((event?: any) => {
    setIsMax(false);
    const newInputValueAndSelection = _getInputValueAndSelection(event);
    if (!newInputValueAndSelection) return;
    setInputValueAndSelection(newInputValueAndSelection);
  }, []);

  function onReset() {
    setInputValueAndSelection({
      inputValue: '',
      inputSelection: { start: 0, end: 0 }
    });
    setValueBig(null);
    setIsMax(false);
    setDisplayValue('');
    setSelection({ start: 0, end: 0 });
  }

  function formatValueNoZero() {
    if (valueBig === null) {
      onReset();
      return;
    }
    const formattedValue = formatAmountByDecimals({
      value: valueBig.toString(),
      decimals,
      trailingZeros: false
    });

    if (!formattedValue) {
      setDisplayValue('');
      return;
    }

    setDisplayValue(formattedValue);
    setSelection({ start: 0, end: 0 });
    setInputValueAndSelection({
      inputValue: formattedValue,
      inputSelection: { start: 0, end: 0 }
    });
  }

  /** Format Input */
  function formatInput(customInput?: { inputValue: string; inputSelection: Selection }) {
    const rawInputValueAndSelection = _getRawInputValueAndSelection(customInput || inputValueAndSelection);
    if (max && new BigNumber(rawInputValueAndSelection.inputValue).toNumber() > max) return;
    const formattedValue = _getFormattedValue(rawInputValueAndSelection.inputValue);
    if (!formattedValue) {
      onReset();
      return;
    }
    const newSelection = _getNewSelection({
      inputSelection: rawInputValueAndSelection.inputSelection,
      formattedValue,
      unformattedValue: rawInputValueAndSelection.inputValue
    });
    setValueBig(new BigNumber(rawInputValueAndSelection.inputValue));
    setDisplayValue(formattedValue);
    setSelection(newSelection);
  }

  /** Format Value */
  function formatValue(value: number | string) {
    const inputValue = typeof value === 'number' ? new BigNumber(value).toString() : value;
    const formattedValue = _getFormattedValue(inputValue);
    setDisplayValue(formattedValue);
    setInputValueAndSelection({
      inputValue: formattedValue,
      inputSelection: { start: formattedValue.length, end: formattedValue.length }
    });
  }

  /**
   * Get Input Value and Selection
   */
  function _getInputValueAndSelection(event: any): RawAmountInputProps | undefined {
    const isIOS = Platform.OS === 'ios';
    if (isIOS) return _getInputValueAndSelectionIOS(event);
    return _getInputValueAndSelectionDefault(event);
  }

  /**
   * Get Input Value and Selection for iOS
   */
  function _getInputValueAndSelectionIOS(event: any): RawAmountInputProps | undefined {
    const {
      nativeEvent: { text, range }
    } = event;

    // If text is empty, return
    if (!range) return;

    // Get cursor's positions
    const start = range.start;
    const end = range.end;

    // insert text into value from cursorStart
    const isDelete = end > start;
    const inputValue = isDelete
      ? displayValue.slice(0, start) + text + displayValue.slice(end)
      : displayValue.slice(0, start) + text + displayValue.slice(start);

    return {
      inputValue,
      inputSelection: {
        start,
        end
      }
    };
  }

  /**
   * Get Input Value and Selection for devices other than IOS
   */
  const _getInputValueAndSelectionDefault = (event: any): RawAmountInputProps | undefined => {
    if (!event.nativeEvent?.target) return;
    const target = event.nativeEvent.target;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const inputValue = target.value;
    return {
      inputValue,
      inputSelection: { start, end }
    };
  };

  /**
   * Remove special characters from input and compute new cursor positions
   * @param params
   */
  function _getRawInputValueAndSelection(params: RawAmountInputProps): RawAmountInputProps {
    if (!params.inputValue) {
      return {
        inputValue: '',
        inputSelection: { start: 0, end: 0 }
      };
    }

    let rawInputValue = params.inputValue.replace(/[^0-9.]/g, '');
    // If inputValue includes .
    if (rawInputValue.includes('.')) {
      const [integerPart, decimalPart, ...rest] = rawInputValue.split('.');
      if (!integerPart) {
        rawInputValue = `.${decimalPart}${rest.join('')}`;
      } else {
        rawInputValue = `${integerPart}.${decimalPart}${rest.join('')}`;
      }
    }

    const start = params.inputSelection.start - (params.inputValue.length - rawInputValue.length);
    const end = params.inputSelection.end - (params.inputValue.length - rawInputValue.length);

    return {
      inputValue: rawInputValue,
      inputSelection: {
        start,
        end
      }
    };
  }

  /**
   * Format value XXX,XXX,XXX,XXX.XXXXXXXX
   * @params value:string, isMax: boolean (default = false)
   * @returns string
   */
  function _getFormattedValue(unformattedValue: string): string {
    const inputValue = unformattedValue.replace(/[^0-9.]/g, '');
    if (!inputValue || inputValue.length === 0) return '';
    const formattedInputValue = formatAmountByDecimals({
      value: inputValue,
      decimals,
      keepZero: true,
      trailingZeros: false
    });
    const result = formattedInputValue;
    return result;
  }

  /**
   * Get new cursor positions { start, end }
   * @param params
   * @returns {
   *  start: number;
   *  end: number;
   * }
   */
  const _getNewSelection = (params: {
    inputSelection: Selection;
    formattedValue: string;
    unformattedValue: string;
  }): {
    start: number;
    end: number;
  } => {
    const { inputSelection, unformattedValue, formattedValue } = params;
    let newSelection = {
      start: inputSelection.start,
      end: inputSelection.end
    };
    if (unformattedValue.length === 0) {
      return newSelection;
    }

    const unformattedValueLength = unformattedValue.length;
    const formattedValueLength = formattedValue.length;
    let offset = formattedValueLength - unformattedValueLength;

    newSelection = {
      start: inputSelection.start + offset,
      end: inputSelection.end + offset
    };

    return {
      start: newSelection.start < 0 ? 0 : newSelection.start,
      end: newSelection.end < 0 ? 0 : newSelection.end
    };
  };

  /**
   * Set Max
   */

  return {
    value: valueBig === null ? null : valueBig.toNumber(),
    valueBig,
    isMax,
    displayValue,
    selection,
    onChange,
    formatValueNoZero,
    formatInput,
    formatValue,
    onSetMax
  };
};
